import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  Form,
  Button,
  InputGroup,
  Toast,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  updateDoc,
  orderBy,
  limit,
  setDoc,
} from "firebase/firestore";
import { useHistory } from "react-router-dom";
import { db } from "../firebase/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
const logo = require("../assets/sphera.png");
const user_iwd = require("../assets/img/user_iwd.png");
import "../css/userPage.css";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { formatFirestoreDate, formatISODate } from "./Dashboard";
const remove_icon = require("../assets/remove.png");
import Autocomplete from "components/AutoComplete";

const cardBodyStyle = {
  display: "flex",
  alignItems: "center",
  border: "1px solid #CE9136",
  borderRadius: "10px",
};

const fetchUserLastTests = async (usersData) => {
  try {
    const userTestsPromises = usersData.map(async (user) => {
      const userTestsQuery = query(
        collection(db, "UserTests"),
        where("userId", "==", user.id),
        orderBy("testDate", "desc"),
        limit(1)
      );
      const userTestsSnapshot = await getDocs(userTestsQuery);
      if (!userTestsSnapshot.empty) {
        return {
          userId: user.id,
          lastTestDate: userTestsSnapshot.docs[0].data().testDate,
        };
      }
      return null;
    });

    const userTests = await Promise.all(userTestsPromises);
    const lastTestsMap = {};
    userTests.forEach((test) => {
      if (test) {
        lastTestsMap[test.userId] = test.lastTestDate;
      }
    });
    return lastTestsMap;
  } catch (error) {
    console.error("Erreur lors de la récupération des derniers tests:", error);
    return {};
  }
};

const fetchParticipantsCount = async (usersData) => {
  try {
    const participantsPromises = usersData.map(async (user) => {
      const participantsQuery = query(
        collection(db, "users"),
        where("referentId", "==", user.id)
      );
      const participantsSnapshot = await getDocs(participantsQuery);
      return {
        userId: user.id,
        count: participantsSnapshot.size,
      };
    });

    const participantsCounts = await Promise.all(participantsPromises);
    const countsMap = {};
    participantsCounts.forEach((item) => {
      countsMap[item.userId] = item.count;
    });
    return countsMap;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération du nombre de participants:",
      error
    );
    return {};
  }
};

const CustomSwitch = ({ userId, isActive, showNotification }) => {
  const [isOn, setIsOn] = useState(isActive);

  const toggleSwitch = async () => {
    setIsOn(!isOn);
    try {
      await updateDoc(doc(db, "users", userId), { isActive: !isOn });
      showNotification(
        "Statut du référent mis à jour avec succès !",
        "success"
      );
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour du statut du référent :",
        error.message
      );
      showNotification(
        `Erreur lors de la mise à jour du statut du référent : ${error.message}`,
        "danger"
      );
    }
  };

  return (
    <div className="switch-container">
      <div className={`switch ${isOn ? "on" : "off"}`} onClick={toggleSwitch}>
        <div className="switch-handle"></div>
      </div>
    </div>
  );
};

const User = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const history = useHistory();
  const [sortOrder, setSortOrder] = useState("asc");
  const [userLastTests, setUserLastTests] = useState({});
  const [sortField, setSortField] = useState("name");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    businessUnit: "",
    company: "",
    companyId: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [participantsCounts, setParticipantsCounts] = useState({});

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersQuery = query(
          collection(db, "users"),
          where("role", "==", "referent")
        );
        const querySnapshot = await getDocs(usersQuery);
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Tri uniquement par nom
        const sortedUsers = usersData.sort((a, b) => {
          const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
          const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
          return sortOrder === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });

        setUsers(sortedUsers);
        setFilteredUsers(sortedUsers);

        // Après avoir récupéré les utilisateurs, récupérez le nombre de participants
        const counts = await fetchParticipantsCount(usersData);
        setParticipantsCounts(counts);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des utilisateurs : ",
          error
        );
      }
    };

    const fetchCompanies = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "companies"));
        const companiesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().companyName,
        }));
        setCompanies(companiesData);
      } catch (error) {
        console.error("Error fetching companies: ", error);
      }
    };

    fetchUsers();
    fetchCompanies();
  }, [sortOrder]);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const queryText = searchQuery; // searchQuery.toLowerCase();

    try {
      // Requêtes de recherche insensibles à la casse
      const lastNameQuery = query(
        collection(db, "users"),
        where("lastName", ">=", queryText),
        where("lastName", "<=", queryText + "\uf8ff")
      );
      const firstNameQuery = query(
        collection(db, "users"),
        where("firstName", ">=", queryText),
        where("firstName", "<=", queryText + "\uf8ff")
      );
      const emailQuery = query(
        collection(db, "users"),
        where("email", ">=", queryText),
        where("email", "<=", queryText + "\uf8ff")
      );

      const [lastNameSnapshot, firstNameSnapshot, emailSnapshot] =
        await Promise.all([
          getDocs(lastNameQuery),
          getDocs(firstNameQuery),
          getDocs(emailQuery),
        ]);

      const lastNameResults = lastNameSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const firstNameResults = firstNameSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const emailResults = emailSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const combinedResults = [
        ...lastNameResults,
        ...firstNameResults,
        ...emailResults,
      ].reduce((acc, user) => {
        if (!acc.find((u) => u.id === user.id)) {
          acc.push(user);
        }
        return acc;
      }, []);

      setFilteredUsers(combinedResults);
    } catch (error) {
      console.error("Error searching users: ", error);
    }
  };

  const handleCompanyClick = (companyId) => {
    if (companyId) {
      history.push(`/admin/entreprise/users?companyId=${companyId}`);
    }
  };

  const handleUserClick = (userId) => {
    if (userId) {
      history.push(`/admin/list/referent/users?userId=${userId}`);
    }
  };

  const handleDeleteUser = (userId) => {
    setSelectedUser(userId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedUser) {
      try {
        // Supprimer l'utilisateur de Firestore
        await deleteDoc(doc(db, "users", selectedUser));

        // Mettre à jour l'interface
        setUsers(users.filter((user) => user.id !== selectedUser));
        setFilteredUsers(
          filteredUsers.filter((user) => user.id !== selectedUser)
        );
        setShowDeleteModal(false);
        setSelectedUser(null);
      } catch (error) {
        console.error(
          "Erreur lors de la suppression de l'utilisateur : ",
          error
        );
      }
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    let formErrors = {};
    if (!formData.firstName) formErrors.firstName = "Le prénom est requis";
    if (!formData.lastName) formErrors.lastName = "Le nom est requis";
    if (!formData.email) formErrors.email = "L'email est requis";
    if (!formData.company) formErrors.company = "Le nom de l'entreprise est requis";
    return formErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length === 0) {
      try {
        setIsLoading(true);

        const tempPassword = Math.random().toString(36).slice(-6);
        let payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: "referent",
          createdAt: new Date().toISOString(),
          password: tempPassword,
          companyName: formData.company,
          isActive: true,
        };

        const newUserRef = doc(collection(db, "users"));
        await setDoc(newUserRef, payload);

        if (formData.company) {
          // Créer la company avec le referentId
          const newCompanyRef = doc(collection(db, "companies"));
          await setDoc(newCompanyRef, {
            companyName: formData.company,
            createdAt: new Date().toISOString(),
            referentId: newUserRef.id, // Utiliser l'id de l'utilisateur créé
          });
          // Récupérer l'id de la company
          const companyId = newCompanyRef.id;
          // Mettre à jour le payload de l'utilisateur avec l'id de la company
          await updateDoc(newUserRef, { companyId });
        }

        // Réinitialiser le formulaire
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          businessUnit: "",
          company: "",
          companyId: "",
        });

        // Actualiser la liste des référents
        const usersQuery = query(
          collection(db, "users"),
          where("role", "==", "referent")
        );
        const querySnapshot = await getDocs(usersQuery);
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Récupérer les derniers tests pour le tri
        const lastTestsMap = await fetchUserLastTests(usersData);
        setUserLastTests(lastTestsMap);

        // Appliquer le tri
        const sortedUsers = usersData.sort((a, b) => {
          const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
          const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
          return sortOrder === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });

        setUsers(sortedUsers);
        setFilteredUsers(sortedUsers);
      } catch (error) {
        console.error("Erreur lors de la création du référent:", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(formErrors);
    }
  };

  const handleBusinessUnitChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      businessUnit: value,
    }));
  };

  const showNotification = (message, variant) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  return (
    <Container>
      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={3000}
        autohide
        style={{
          position: "absolute",
          top: 20,
          right: 20,
        }}
        bg={toastVariant}>
        <Toast.Header>
          <strong className="me-auto">Notification</strong>
        </Toast.Header>
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>

      <Row className="my-4 align-items-center">
        <Col md={6} style={{ display: "flex", flexDirection: "row", gap: 20 }}>
          <img
            src={logo}
            alt="Insights"
            className="img-fluid"
            style={{ height: 158 }}
          />
          <div>
            <h1 className="title">Référents</h1>
            <span className="subtitle"></span>
          </div>
        </Col>
        <Col
          md={6}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}>
          <Form
            inline
            style={{ display: "flex", flex: 1, alignItems: "center" }}
            onSubmit={handleSearch}>
            <Form.Control
              type="text"
              placeholder="Recherche"
              className=""
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <button
              type="submit"
              style={{
                backgroundColor: "rgba(206, 145, 54, 0.36)",
                border: "none",
                borderRadius: "5px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#CE9136",
                height: "40px",
                width: "40px",
                alignSelf: "flex-end",
                marginLeft: 6,
              }}>
              <i
                className="fa fa-search"
                aria-hidden="true"
                style={{ fontSize: 20 }}></i>
            </button>
          </Form>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Row className="mt-4 align-items-center">
            <Col>
              <span className="title-kpi">
                Liste des référents{" "}
                <span style={{ color: "#CE9136" }}>existantes</span>
              </span>
            </Col>
            <Col xs="auto">
              <Button variant="link" onClick={toggleSortOrder}>
                {sortOrder === "asc"
                  ? "Trier par nom A à Z"
                  : "Trier par nom Z à A"}
              </Button>
            </Col>
          </Row>
          <Card className="card">
            <Card.Body>
              <Table hover>
                <thead>
                  <tr>
                    <th>Select user</th>
                    <th>Nom / Prénom</th>
                    <th>Date d'inscription</th>
                    <th>Actif</th>
                    <th>Nombre de participants</th>
                    <th>Entreprise</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td
                        className="text-center align-middle"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleUserClick(user.id)}>
                        <span className="img-conent">
                          <img src={user_iwd} alt="user_iwd" className="icon" />
                        </span>
                      </td>
                      <td>{`${user.lastName} ${user.firstName}`}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        <CustomSwitch
                          userId={user.id}
                          isActive={user.isActive !== false}
                          showNotification={showNotification}
                        />
                      </td>
                      <td>{participantsCounts[user.id] || 0}</td>
                      <td
                        className="bold-brown"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleCompanyClick(user.company)}>
                        {user.companyName || "-"}
                      </td>
                      <td>
                        <img
                          src={remove_icon}
                          alt="Supprimer"
                          style={{ cursor: "pointer", width: "20px" }}
                          onClick={() => handleDeleteUser(user.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {/*  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Card.Link href="#" style={{ textDecoration: "underline" }}>
                  Pagination ici ..
                </Card.Link>
              </div> */}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Form
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          padding: "20px",
          marginTop: "20px",
        }}
        onSubmit={handleSubmit}>
        <Form.Label
          style={{
            fontWeight: "bold",
            fontSize: "18px",
            fontFamily: "Poppins",
          }}>
          Ajouter un <span style={{ color: "#CE9136" }}>référent</span>
        </Form.Label>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Prénom"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                style={{ borderColor: "#CE9136", width: "100%" }}
                className="custom-placeholder"
              />
              {errors.firstName && (
                <Form.Text className="text-danger">
                  {errors.firstName}
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Nom de famille"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                style={{ borderColor: "#CE9136", width: "100%" }}
                className="custom-placeholder"
              />
              {errors.lastName && (
                <Form.Text className="text-danger">{errors.lastName}</Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                placeholder="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{ borderColor: "#CE9136", width: "100%" }}
                className="custom-placeholder"
              />
              {errors.email && (
                <Form.Text className="text-danger">{errors.email}</Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Nom de l'entreprise"
                name="company"
                value={formData.company}
                onChange={handleChange}
                style={{ borderColor: "#CE9136", width: "100%" }}
                className="custom-placeholder"
              />
              {errors.company && (
                <Form.Text className="text-danger">{errors.company}</Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Form.Text
          className=""
          style={{
            fontSize: "14px",
            color: "#151515",
            fontFamily: "Poppins",
            fontStyle: "italic",
            display: "flex",
            marginBottom: "20px",
          }}>
          Créez un nouveau référent pour permettre aux candidats de le
          sélectionner lors de leur inscription. Le référent recevra par e-mail
          son mot de passe généré pour pouvoir accéder à sa plateforme.
        </Form.Text>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="primary"
            type="submit"
            style={{
              backgroundColor: "#CE9136",
              borderColor: "#CE9136",
              color: "#fff",
              fontFamily: "Poppins",
            }}>
            {isLoading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </Form>
      <ConfirmDeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={confirmDelete}
        title={"Supprimer définitivement cette utilisateur ?"}
        companyName={""}
      />
    </Container>
  );
};

export default User;
